import React from "react";
import MaskReveal from "../../shared/components/UIElements/MaskReveal";
import Reveal from "../../shared/components/UIElements/Reveal";
import CadenzaDemo from "../components/CadenzaDemo";
import FeatureIcon from "../components/FeatureIcon";
import "./styling/CadenzaSection.css";

const REACH = [
  "Scenarios & Configurations",
  "Simulation Results",
  "Heap Snapshots",
  "Operational Data",
  "Maintenance Logs",
  "Water Balance",
  "Custom Exports",
  "Leach Schedules",
];

const GUARDRAILS = [
  {
    icon: "building",
    title: "Your Permissions, Exactly",
    body:
      "Cadenza acts as you. It reads through the same role-based access control " +
      "as the web app, using your own signed-in identity — so it can never reach " +
      "a project, company or scenario you couldn't already open yourself.",
  },
  {
    icon: "link",
    title: "Recorded, Not Opaque",
    body:
      "Every tool call is written to an audit trail an administrator can review — " +
      "who asked, which tool ran, and whether it succeeded. The assistant cannot " +
      "read or edit its own trail.",
  },
  {
    icon: "calibration",
    title: "Changes Stay Sandboxed",
    body:
      "Read access is the default. Editing is a separate permission, confined to " +
      "scenarios the assistant created itself, and every one is badged ✦ AI in the " +
      "app so it is obvious what to review. Simulations still have to be run by a person.",
  },
  {
    icon: "cloud",
    title: "Off by Company, If You Prefer",
    body:
      "Sending project data to an AI assistant is a governance decision, not just " +
      "a technical one. Any company can switch the integration off from its own " +
      "settings, so adopting Cadenza stays a choice each organisation makes for itself.",
  },
];

/**
 * CadenzaSection — homepage section for the Cadenza MCP integration.
 *
 * Framing matters here: Cadenza is a server, not a chatbot. Orecasting does not
 * ship an in-app assistant — it exposes the project's data to whichever
 * MCP-capable AI client the engineer already uses. The copy leads with that so
 * nobody arrives expecting a chat window inside the app.
 */
export default function CadenzaSection() {
  return (
    <section className="cadenza-section" aria-labelledby="cadenza-heading">
      <MaskReveal as="h2" id="cadenza-heading" className="section-heading">
        Cadenza — Ask Your Project Anything
      </MaskReveal>

      <Reveal as="p" className="cadenza-section__intro">
        Orecasting holds the model, the results, the site record and the
        maintenance history. Cadenza opens all of it to the AI assistant you
        already work with, so the questions that used to mean an afternoon of
        cross-referencing spreadsheets can simply be asked.
      </Reveal>

      <div className="cadenza-section__block">
        <Reveal className="cadenza-section__copy">
          <span className="cadenza-section__eyebrow">Built In, Not Bolted On</span>
          <h3>Your assistant, connected to your data</h3>
          <p>
            Cadenza is a Model Context Protocol server built into the Orecasting
            platform. Connect a client that speaks MCP — Claude, or any other —
            authorise it once through a standard OAuth sign-in, and it gains a
            set of tools for querying your projects directly.
          </p>
          <p>
            Because answers are assembled from tool calls against your actual
            records rather than from a summary pasted into a prompt, they cite
            real numbers: a specific simulation, a specific date range, a
            specific asset. And the reach is cross-cutting — the same
            conversation can compare two scenarios, pull the irrigation you
            actually logged, and check what broke that week.
          </p>
          <p className="cadenza-section__aside">
            There is no chat window inside Orecasting. Cadenza deliberately
            works through the assistant you have already chosen and already
            trust, rather than adding one more.
          </p>
          <ul className="cadenza-section__pills">
            {REACH.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </Reveal>

        <Reveal className="cadenza-section__demo" delay={90}>
          <CadenzaDemo />
        </Reveal>
      </div>

      <div className="cadenza-section__grid">
        {GUARDRAILS.map((g, i) => (
          <Reveal key={g.title} className="cadenza-section__card" delay={i * 70}>
            <span className="cadenza-section__card-icon" aria-hidden="true">
              <FeatureIcon name={g.icon} />
            </span>
            <h3>{g.title}</h3>
            <p>{g.body}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
